import { Component, Entity, registerComponent } from "aframe";
import { AmmoBodyComponent, AmmoCollisionObject, AmmoGlobal, AmmoPhysicsSystem } from "./ammo";
import { AvatarSystem, CollisionReaction } from "./avatar";


/*

"collision" component : detects collisions with other bodies to emit enter/exit events

*/

declare var Ammo : AmmoGlobal;

interface CollisionComponentData
{
	body : string[];
	enter : string;
	exit : string;
	bubbles : boolean;
	collisionGroup : string[];
	collisionMask : string[];
	condition : string[];
}

interface CollisionComponent extends Component
{
	data:CollisionComponentData;
	reaction : CollisionReaction;
	bodies : number [ ];
	previousContacts? : Entity[];
	currentContacts : Entity[];
}

registerComponent("collision", {
	schema : {
		body : { type : "array", default : ["ammo-body"] },
		enter : { type : "string" },
		exit : { type : "string" },
		bubbles : { type : "boolean", default : false },
		collisionGroup : { type : "array" },
		collisionMask : { type : "array" },
		condition : { type : "array", default : [] }
	},
	multiple : true,
	init(this : CollisionComponent)
	{
		this.bodies = [];
		this.currentContacts = [];

		const scene = this.el.sceneEl!;
		const avatarSystem = scene.systems.avatar as AvatarSystem;

		this.el.addEventListener("componentinitialized", (event) =>
		{
			if(event.detail.name != "ammo-body") return;
			if(!this.reaction) return;
			const attrName = event.detail.id ? ("ammo-body__" + event.detail.id) : "ammo-body";
			if(!this.data.body.includes(attrName)) return;
			const bodyComp = this.el.components[attrName] as AmmoBodyComponent;
			bodyComp.bodyInitialized.then( (rigidBody) => {
				const ptr = Ammo.getPointer(rigidBody);
				this.bodies.push(ptr);
				avatarSystem.collisionReactions[ptr] = this.reaction;
				rigidBody.activate(true);
			} );
		} );
	},

	remove(this : CollisionComponent)
	{
		const scene = this.el.sceneEl!;
		const avatarSystem = scene.systems.avatar as AvatarSystem;
		for(let ptr of this.bodies.splice(0, this.bodies.length))
		{
			delete avatarSystem.collisionReactions[ptr];
		}
	},
	
	update(this : CollisionComponent, oldData : CollisionComponentData )
	{
		const scene = this.el.sceneEl!;
		const avatarSystem = scene.systems.avatar as AvatarSystem;
		const physics = scene.systems.physics as AmmoPhysicsSystem;

		const collisionGroup = physics.getCollisionFilter(this.data.collisionGroup);
		const collisionMask = physics.getCollisionFilter(this.data.collisionMask);

		const condition = this.data.condition;

		this.reaction = (phase : number, other? : AmmoCollisionObject) =>
		{
			if(phase < 0) //Pre-collision phase
			{
				if(this.previousContacts) return; //Already done
				this.previousContacts = this.currentContacts.splice(0, this.currentContacts.length);
				return;
			}

			if(phase > 0) //Post-collision phase
			{
				if(!this.previousContacts) return; //Already done

				if(this.data.enter) 
					for(let c of this.currentContacts)
					{
						if(this.previousContacts.includes(c)) continue;
						this.el.emit(this.data.enter, c, this.data.bubbles);
					}
				if(this.data.exit)
					for(let c of this.previousContacts)
					{
						if(this.currentContacts.includes(c)) continue;
						this.el.emit(this.data.exit, c, this.data.bubbles);
					}

				delete this.previousContacts;
				return;
			}

			if(collisionGroup || collisionMask)
			{
				const otherBH = other!.getBroadphaseHandle();
				if(collisionGroup && ! (otherBH.m_collisionFilterGroup & collisionGroup)) return;
				if(collisionMask && ! (otherBH.m_collisionFilterMask & collisionMask)) return;
			}	
			
			const otherBodyComp = physics.components[ Ammo.getPointer(other) ];
			if(!otherBodyComp) return;

			const otherEl = otherBodyComp.el;
			if(this.currentContacts.includes(otherEl)) return;

			if(condition && condition.length)
			{
				for(let c of condition)
					if(otherEl.matches(c))
						return this.currentContacts.push(otherEl);
			}
			else
				return this.currentContacts.push(otherEl);
		};

		for(let ptr of this.bodies.splice(0, this.bodies.length))
		{
			delete avatarSystem.collisionReactions[ptr];
		}

		for(let attrName of this.data.body)
		{
			const bodyComp = this.el.components[attrName] as AmmoBodyComponent;
			bodyComp.initialized && bodyComp.bodyInitialized.then( (rigidBody) => {
				const ptr = Ammo.getPointer(rigidBody);
				this.bodies.push(ptr);
				avatarSystem.collisionReactions[ ptr ] = this.reaction;
				rigidBody.activate(true);
			} );
		}
	}
})